import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { FolderHeader } from '@/features/folder/FolderHeader';
import { ProjectFileList } from './ProjectFileList';
import { setItemsForParent, selectItemsForParent } from '@/store/itemsSlice';
import { getProjectFilesNormalized } from '@/api/project';
import { useViewMode } from '@/hooks/useViewMode';
import type { NormalizedItem } from '@/components/itemList/types';
import ItemListLoading from '../../components/itemListLoading/ItemListLoading';

interface ProjectFilesViewProps {
  fileListView?: string;
}

export function ProjectFilesView({ fileListView }: ProjectFilesViewProps) {
  const [sortByCriteriaIndex, setSortByCriteriaIndex] = useState(0);
  const [sortAscending, setSortAscending] = useState(true);
  const [viewMode, setViewMode] = useViewMode('project-files');
  const { id: projectId, viewType } = useParams<{ id: string; viewType?: string }>();
  const items = useAppSelector(selectItemsForParent(projectId)) as NormalizedItem[] | undefined;
  const dispatch = useAppDispatch();

  const filteredItems = useMemo(() => {
    const view = viewType || fileListView;
    if (!items) return null;
    return view === 'allfiles' ? items : items.filter((i) => i.type === view);
  }, [viewType, fileListView, items]);

  const refreshItems = () => {
    if (!projectId) return;
    setTimeout(() => {
      getProjectFilesNormalized(projectId).then((normalized) => {
        dispatch(setItemsForParent({ parentId: projectId, items: normalized }));
      });
    }, 1000);
  };

  return (
    <div className="mt-4">
      <FolderHeader
        sortCriteriaIndex={sortByCriteriaIndex}
        sortAscending={sortAscending}
        showTypes={[]}
        onSortCriteriaChange={() => setSortByCriteriaIndex((i) => (i >= 2 ? 0 : i + 1))}
        onSortDirectionToggle={() => setSortAscending((v) => !v)}
        onShowTypesChange={() => {}}
        showTypeFilter={false}
        disabled={!filteredItems}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
      />
      {!filteredItems ? (
        <ItemListLoading amount={12} mode={viewMode} />
      ) : (
        <ProjectFileList
          items={filteredItems}
          sortCriteriaIndex={sortByCriteriaIndex}
          sortAscending={sortAscending}
          onRefresh={refreshItems}
          projectId={projectId}
          viewMode={viewMode}
        />
      )}
    </div>
  );
}
